import { useEffect, useMemo } from "react";
import { useLocation, useMatch, useParams } from "react-router";

import { useGetCustomMapsInfiniteQuery } from "api/topicsApi.slice";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTrack } from "common/utilities/useTrack";
import { toggleCustomKeyIssueForm } from "domains/customKeyIssue/actions";
import { activateModal } from "domains/modal/actions";
import { getTopicPageEvent } from "domains/topic/analytics";
import { TopicDetail } from "domains/topicDetail/components/TopicDetail";
import { useFetchTopicContentMeta } from "domains/topicSearchFilter/useTopicContentMeta";
import { setTransformationMapInnerNode } from "domains/transformationMap/actions";
import { getTmSelectedInnerNode, getTmTopic } from "domains/transformationMap/selectors";
import { getCanAccessBriefing, getCreateMapLimit, getIsCreateMapRestricted } from "entities/permissions/selectors";

type TopicDetailPageProps = { launchBriefing?: boolean };

export const TopicDetailPage = ({ launchBriefing }: TopicDetailPageProps) => {
  const dispatch = useDispatch();
  const go = useGo();
  const { pathname } = useLocation();
  const createMatch = useMatch("/create/new");
  const { keyIssueId, topicId } = useParams();
  const canAccessBriefing = useSelector(getCanAccessBriefing);
  const topic = useSelector(getTmTopic);
  const selectedKeyIssue = useSelector(getTmSelectedInnerNode);
  const track = useTrack();

  const { data: paginatedCustomMaps } = useGetCustomMapsInfiniteQuery(undefined);
  const customMaps = useMemo(() => paginatedCustomMaps?.pages.flat(), [paginatedCustomMaps?.pages]);
  const mapLimit = useSelector(getCreateMapLimit);
  const isCreateMapRestricted = useSelector(getIsCreateMapRestricted);

  const canCreate = !isCreateMapRestricted || mapLimit === null || !customMaps || customMaps.length < mapLimit;

  useEffect(() => {
    const isCreatingNewTopic = pathname.startsWith("/create/new");
    if (isCreatingNewTopic && !canCreate)
      dispatch(activateModal({ id: "CreateMapUpgrade", params: { onDismissRoute: "/create" } }));
  }, [canCreate, dispatch, pathname]);

  useEffect(() => {
    if (!launchBriefing || topic?.id === undefined) return;
    const basePath = pathname.slice(0, -"/briefing".length);
    if (canAccessBriefing) {
      dispatch(
        activateModal({
          id: "Briefing",
          params: { onDismissRoute: basePath, topicId: topic?.id, topicName: topic?.name },
        }),
      );
      return;
    }
    go(basePath, { replace: true });
  }, [canAccessBriefing, dispatch, go, launchBriefing, pathname, topic?.id, topic?.name]);

  useEffect(() => {
    if (topic === undefined) return;
    const event = getTopicPageEvent(topic, selectedKeyIssue);
    const isCreate = pathname.startsWith("/create");
    if (isCreate) event.page = "Create";
    track(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic?.id, selectedKeyIssue?.id, track]);

  useFetchTopicContentMeta();

  useEffect(() => {
    if (keyIssueId && selectedKeyIssue?.id !== keyIssueId) {
      dispatch(setTransformationMapInnerNode({ id: keyIssueId, topicId }));
    } else if (!keyIssueId && selectedKeyIssue) {
      dispatch(setTransformationMapInnerNode({ id: undefined, topicId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyIssueId]);

  useEffect(() => {
    dispatch(toggleCustomKeyIssueForm({ isActive: false }));
  }, [dispatch]);

  return <TopicDetail isNew={!!createMatch} />;
};
